// LeaseLifecycleActions — Send / Cancel / Cancel & resend / Revise & keep open
// / Refresh status, scoped to a single lease version (§2).
//
// Two phases of the lifecycle are represented:
//   1. Pre-send (generated, not yet sent): a single "Send for signature"
//      action with an options popover (send email, document type).
//   2. In-flight (sent, partially signed, etc.): three clear choices —
//      Cancel, Cancel & resend, Revise & keep open — plus a manual Refresh.
//      When a "Revise & keep open" has regenerated a NEWER version than the
//      kept-open submission, a "Send revision for signature" action renders
//      alongside those (see revisionSendReady) — it is the same send with
//      `confirmNewSend: true`, which the API's group send-claim requires
//      before it will mint a second live lease for the group.
//
// All server calls are isolated in hooks/useLeaseLifecycle.ts so contract
// drift with the backend agent is reconciled in one place. Results surface via
// the DS toast host; destructive actions route through ConfirmDialog.

import { useState } from 'react';
import { toast } from '@/components/Toast';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ApiError } from '@/lib/api';
import {
  sendForSignature,
  reviseLease,
  refreshStatus,
  classifyLeaseStatus,
  leaseStatusLabel,
  buildSendForSignatureBody,
  revisionSendReady,
  type LeaseLifecycleResult,
  type LeaseSignatureRecordSummary,
  type LeaseStatus,
  type ReviseAction,
  type ReviseResult,
  type TestSigner,
} from '@/hooks/useLeaseLifecycle';

/** The in-flight revise actions exposed as buttons (cancel has its own button). */
type ReviseButton = 'cancel-resend' | 'keep-open';

interface LeaseLifecycleActionsProps {
  groupId: string;
  versionId: string;
  /**
   * Current lease status OBJECT (ComputedLeaseStatus) — drives which phase of
   * actions is shown. Keyed off `status`; the label is for display only.
   */
  status: LeaseStatus | null | undefined;
  /** Test-lease override passed straight through to the send call (§5). */
  testSigners?: TestSigner[];
  /** Notified with the API's resulting status so the caller can refresh. */
  onResult?: (result: LeaseLifecycleResult) => void;
  /**
   * Called when a revise action signals `regenerate: true` (cancel-resend /
   * keep-open): the parent should regenerate a new version + send it. The
   * `keepOpen` flag is true when the new send coexists with the older open one.
   */
  onRegenerate?: (info: { action: ReviseAction; keepOpen: boolean }) => void;
  /** Compact (table-cell) layout uses data-size="sm" buttons. */
  size?: 'sm' | 'md';
  /**
   * Whether the generated lease is CURRENT (no edits since it was generated).
   * The "Send for signature" action only appears when this is true — sending a
   * stale PDF after the form was edited would ship the wrong document; the user
   * must regenerate first. Defaults to true so other (table) callers are
   * unaffected. In-flight actions (Cancel / Revise / Refresh) ignore this.
   */
  canSend?: boolean;
  /**
   * The NEWEST raw signature record from GET …/status (`signatureRecord`),
   * narrowed via pickSignatureRecordSummary. Drives the in-flight "Send
   * revision for signature" action: it renders only when this record is OPEN
   * and `keptOpen`, `versionId` (the draft's latest generated version) is
   * NEWER than the record's, and `canSend`. Omit/null → never shown.
   */
  signatureRecord?: LeaseSignatureRecordSummary | null;
}

type Busy = null | 'send' | 'cancel' | 'cancel-resend' | 'revise' | 'refresh';

function errMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; message?: string } | null;
    return body?.error ?? body?.message ?? `${fallback} (HTTP ${err.status})`;
  }
  return err instanceof Error ? err.message : fallback;
}

/** Pull a human-readable status label from a result whose `status` may be a
 *  string (revise/refresh) or a LeaseStatus object (send). */
function resultStatusLabel(result: LeaseLifecycleResult): string {
  const s = result.status;
  if (typeof s === 'string') return s;
  if (s && typeof s === 'object') return leaseStatusLabel(s);
  return '';
}

function surfaceResult(result: LeaseLifecycleResult, verb: string): void {
  // A missing DOB does NOT gate signing — the adults-only filter treats a
  // missing/blank DOB as an adult and includes the signer, so DOB is never
  // required to send for signature (WEL-27). Just confirm success.
  const label = resultStatusLabel(result);
  const statusLabel = label ? ` Status: ${label}.` : '';
  toast.success(`${verb}.${statusLabel}`);
}

export function LeaseLifecycleActions({
  groupId,
  versionId,
  status,
  testSigners,
  onResult,
  onRegenerate,
  size = 'sm',
  canSend = true,
  signatureRecord = null,
}: LeaseLifecycleActionsProps): React.JSX.Element {
  const [busy, setBusy] = useState<Busy>(null);
  const [showSendOptions, setShowSendOptions] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);

  // Destructive-action confirmation: 'cancel' | 'cancel-resend' | 'keep-open'.
  const [confirm, setConfirm] = useState<null | ReviseButton | 'cancel'>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const cls = classifyLeaseStatus(status);
  const sizeAttr = size === 'sm' ? 'sm' : undefined;

  // Phase selector: pre-send vs in-flight vs terminal.
  const isPreSend = cls.kind === 'new' || cls.kind === 'resume-draft' || cls.kind === 'generated'
    || cls.kind === 'revision-resume-draft' || cls.kind === 'revision-generated'
    || cls.kind === 'cancelled-resume-draft';
  const isTerminal = cls.kind === 'fully-executed' || cls.kind === 'cancelled';
  const isInFlight = !isPreSend && !isTerminal;

  // In-flight revision ready to go out: the kept-open submission is still the
  // group's newest record, but `versionId` (the regenerated PDF) is newer than
  // it. Same canSend gate as the pre-send button (current PDF, no blockers).
  const isRevisionReady = isInFlight && canSend && revisionSendReady(signatureRecord, versionId);

  const runSend = async () => {
    setBusy('send');
    setShowSendOptions(false);
    try {
      const result = await sendForSignature(
        groupId,
        versionId,
        buildSendForSignatureBody({ sendEmail, testSigners, revision: isRevisionReady }),
      );
      surfaceResult(result, isRevisionReady ? 'Revision sent for signature' : 'Sent for signature');
      onResult?.(result);
    } catch (err) {
      toast.error(errMessage(err, 'Could not send for signature'), { title: 'Send failed' });
    } finally {
      setBusy(null);
    }
  };

  const runRefresh = async () => {
    setBusy('refresh');
    try {
      const result = await refreshStatus(groupId, versionId);
      const label = resultStatusLabel(result);
      toast.info(label ? `Status refreshed: ${label}` : 'Status refreshed.');
      onResult?.(result);
    } catch (err) {
      toast.error(errMessage(err, 'Could not refresh status'), { title: 'Refresh failed' });
    } finally {
      setBusy(null);
    }
  };

  const runConfirmedAction = async () => {
    if (!confirm) return;
    setConfirmError(null);
    try {
      if (confirm === 'cancel') {
        // GROUP-level revise so ALL open submissions are cancelled — not just
        // the newest-for-version. Matters in a hold-and-send multi-active state
        // where older opens would otherwise stay live and still signable.
        setBusy('cancel');
        const result: ReviseResult = await reviseLease(groupId, 'cancel');
        surfaceResult(result as unknown as LeaseLifecycleResult, 'Lease cancelled');
        onResult?.(result as unknown as LeaseLifecycleResult);
        setConfirm(null);
        return;
      }
      // 'keep-open' | 'cancel-resend' — GROUP-level revise.
      setBusy(confirm === 'keep-open' ? 'revise' : 'cancel-resend');
      const result: ReviseResult = await reviseLease(groupId, confirm);
      surfaceResult(
        result as unknown as LeaseLifecycleResult,
        confirm === 'keep-open' ? 'Revision started (signatures kept open)' : 'Cancelled & resent',
      );
      onResult?.(result as unknown as LeaseLifecycleResult);
      setConfirm(null);
      // When the backend signals regenerate, hand off to the parent to
      // regenerate a new version + send it (the revise route only cancels /
      // keeps-open; it does not itself regenerate).
      if (result.regenerate) {
        onRegenerate?.({ action: result.action, keepOpen: result.keepOpen });
      }
    } catch (err) {
      setConfirmError(errMessage(err, 'Action failed'));
    } finally {
      setBusy(null);
    }
  };

  const confirmCopy: Record<NonNullable<typeof confirm>, { title: string; description: string; label: string; busyLabel: string; tone: 'danger' | 'primary' }> = {
    cancel: {
      title: 'Cancel this lease?',
      description:
        'Voids the signing request for all parties. Signers will be notified the request was cancelled. This cannot be undone.',
      label: 'Cancel lease',
      busyLabel: 'Cancelling…',
      tone: 'danger',
    },
    'cancel-resend': {
      title: 'Cancel & resend?',
      description:
        'Voids the current signing request and immediately sends a fresh one to all signers. Use this when the document needs changes that require everyone to re-sign.',
      label: 'Cancel & resend',
      busyLabel: 'Resending…',
      tone: 'danger',
    },
    'keep-open': {
      title: 'Revise & keep open?',
      description:
        'Opens the draft for edits while the existing signing request stays open. Signers who have already signed keep their signatures.',
      label: 'Start revision',
      busyLabel: 'Starting…',
      tone: 'primary',
    },
  };

  // The Send action + its options popover. Rendered on the pre-send path
  // (label "Send for signature") and on the in-flight revision path (label
  // "Send revision for signature"); runSend decides the body from
  // isRevisionReady, so both share one handler.
  const sendGroup = (label: string) => (
    <div style={{ position: 'relative', display: 'inline-flex', gap: 'var(--space-2)' }}>
      <button
        type="button"
        className="btn"
        data-size={sizeAttr}
        disabled={busy !== null}
        onClick={() => void runSend()}
      >
        {busy === 'send' ? 'Sending…' : label}
      </button>
      <button
        type="button"
        className="btn"
        data-variant="secondary"
        data-size={sizeAttr}
        data-icon-only
        aria-label="Send options"
        aria-expanded={showSendOptions}
        disabled={busy !== null}
        onClick={() => setShowSendOptions((v) => !v)}
      >
        ⋯
      </button>
      {showSendOptions && (
        <div
          className="popover"
          role="group"
          aria-label="Send options"
          style={{ position: 'absolute', top: 'calc(100% + var(--space-2))', right: 0, zIndex: 'var(--z-overlay)' }}
        >
          <div className="stack-sm">
            <label className="choice">
              <input
                type="checkbox"
                className="check"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
              />
              Send notification email to signers
            </label>
            <button
              type="button"
              className="btn"
              data-size="sm"
              disabled={busy !== null}
              onClick={() => void runSend()}
              style={{ alignSelf: 'flex-start' }}
            >
              Send now
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 'var(--space-3)', alignItems: 'center' }}>
      {/* ── Pre-send: single Send action + options. Only when the generated
          lease is CURRENT (canSend) — after edits the user must regenerate. ── */}
      {isPreSend && canSend && sendGroup('Send for signature')}

      {/* ── In-flight revision: a "Revise & keep open" regenerated a NEWER
          version than the kept-open submission — offer to send it (with
          confirmNewSend) alongside the in-flight actions. ─────────────── */}
      {isRevisionReady && sendGroup('Send revision for signature')}

      {/* ── In-flight: three clear choices + manual refresh ─────────────── */}
      {isInFlight && (
        <>
          <button
            type="button"
            className="btn"
            data-variant="danger-ghost"
            data-size={sizeAttr}
            disabled={busy !== null}
            onClick={() => { setConfirm('cancel'); setConfirmError(null); }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn"
            data-variant="secondary"
            data-size={sizeAttr}
            disabled={busy !== null}
            onClick={() => { setConfirm('cancel-resend'); setConfirmError(null); }}
          >
            Cancel &amp; resend
          </button>
          <button
            type="button"
            className="btn"
            data-variant="secondary"
            data-size={sizeAttr}
            disabled={busy !== null}
            onClick={() => { setConfirm('keep-open'); setConfirmError(null); }}
          >
            Revise &amp; keep open
          </button>
        </>
      )}

      {/* Manual status refresh — available whenever a version exists. */}
      {!isTerminal && (
        <button
          type="button"
          className="btn"
          data-variant="ghost"
          data-size={sizeAttr}
          disabled={busy !== null}
          onClick={() => void runRefresh()}
          title="Reconcile status with DocuSeal"
        >
          {busy === 'refresh' ? 'Refreshing…' : 'Refresh'}
        </button>
      )}

      <ConfirmDialog
        open={confirm !== null}
        onOpenChange={(open) => { if (!open) { setConfirm(null); setConfirmError(null); } }}
        title={confirm ? confirmCopy[confirm].title : ''}
        {...(confirm ? { description: confirmCopy[confirm].description } : {})}
        confirmLabel={confirm ? confirmCopy[confirm].label : 'Confirm'}
        {...(confirm ? { busyLabel: confirmCopy[confirm].busyLabel } : {})}
        tone={confirm ? confirmCopy[confirm].tone : 'danger'}
        busy={busy !== null && busy !== 'refresh' && busy !== 'send'}
        error={confirmError}
        onConfirm={() => void runConfirmedAction()}
      />
    </div>
  );
}
